<script setup lang="ts">
import { computed } from 'vue'

import BetaSuggestionCard from '@/components/beta-dashboard/molecules/BetaSuggestionCard.vue'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type { BetaAiSuggestion } from '@/types/beta-dashboard'

const emit = defineEmits<{
    action: [suggestion: BetaAiSuggestion]
}>()

const betaStore = useBetaDashboardStore()

const generatedLabel = computed(() => {
    if (!betaStore.analysis?.generatedAt) return ''

    const date = new Date(betaStore.analysis.generatedAt)
    if (Number.isNaN(date.getTime())) return ''

    return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(date)
})

const analyze = (refresh = false) => {
    void betaStore.analyzeSite(refresh)
}
</script>

<template>
    <section class="beta-ai-guidance">
        <header class="beta-ai-guidance__heading">
            <div>
                <p>Sugestões para melhorar seu site</p>
                <h2>Orientação simples, baseada no seu conteúdo</h2>
                <span>
                    A análise apenas recomenda próximos passos. Nenhuma alteração é feita automaticamente.
                </span>
            </div>

            <button
                v-if="betaStore.analysis"
                type="button"
                :disabled="betaStore.loadingAnalysis"
                @click="analyze(true)"
            >
                {{ betaStore.loadingAnalysis ? 'Analisando…' : 'Atualizar sugestões' }}
            </button>
        </header>

        <div v-if="betaStore.loadingAnalysis && !betaStore.analysis" class="beta-ai-guidance__grid">
            <div v-for="index in 3" :key="index" class="beta-ai-guidance__skeleton" />
        </div>

        <div
            v-else-if="!betaStore.analysis && !betaStore.analysisConsent"
            class="beta-ai-guidance__intro"
        >
            <span class="beta-ai-guidance__intro-icon" aria-hidden="true">✦</span>
            <div>
                <strong>Quer uma segunda opinião sobre o site?</strong>
                <p>
                    Vamos analisar somente as informações públicas das páginas, sem enviar
                    scripts, integrações, mensagens recebidas ou dados administrativos.
                </p>
            </div>
            <button type="button" @click="analyze(false)">Analisar meu site</button>
        </div>

        <div v-else-if="betaStore.analysisError && !betaStore.analysis" class="beta-ai-guidance__error">
            <div>
                <strong>A análise está temporariamente indisponível.</strong>
                <p>{{ betaStore.analysisError }}</p>
            </div>
            <button type="button" @click="analyze(false)">Tentar novamente</button>
        </div>

        <template v-else-if="betaStore.analysis">
            <div v-if="betaStore.analysis.suggestions.length" class="beta-ai-guidance__grid">
                <BetaSuggestionCard
                    v-for="suggestion in betaStore.analysis.suggestions"
                    :key="suggestion.id"
                    :suggestion="suggestion"
                    @action="emit('action', $event)"
                />
            </div>

            <div v-else class="beta-ai-guidance__empty">
                <span aria-hidden="true">✓</span>
                <div>
                    <strong>Seu conteúdo cobre os principais pontos.</strong>
                    <p>Continue revisando o site sempre que atualizar suas páginas.</p>
                </div>
            </div>

            <footer class="beta-ai-guidance__meta">
                <span v-if="betaStore.analysis.cached">Resultado salvo</span>
                <span v-if="generatedLabel">Analisado em {{ generatedLabel }}</span>
            </footer>
        </template>
    </section>
</template>

<style scoped>
.beta-ai-guidance {
    display: grid;
    gap: 17px;
}

.beta-ai-guidance__heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 20px;
}

.beta-ai-guidance__heading p {
    color: var(--main-color);
    font-size: 12px;
    font-weight: 850;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-ai-guidance__heading h2 {
    margin-top: 7px;
    color: #172132;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.02em;
}

.beta-ai-guidance__heading span {
    display: block;
    margin-top: 5px;
    color: #7a899e;
    font-size: 11px;
}

.beta-ai-guidance__heading > button,
.beta-ai-guidance__intro > button,
.beta-ai-guidance__error > button {
    flex: none;
    border-radius: var(--beta-button-radius);
    background: color-mix(in srgb, var(--main-color) 9%, white);
    padding: 10px 13px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 850;
}

.beta-ai-guidance__heading > button:disabled {
    cursor: wait;
    opacity: 0.55;
}

.beta-ai-guidance__intro,
.beta-ai-guidance__error,
.beta-ai-guidance__empty {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    border: 1px solid #dfe7f0;
    border-radius: 20px;
    background: #fff;
    padding: 20px;
}

.beta-ai-guidance__intro-icon,
.beta-ai-guidance__empty > span {
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border-radius: 14px;
    background: color-mix(in srgb, var(--main-color) 10%, white);
    color: var(--main-color);
    font-size: 18px;
    font-weight: 850;
}

.beta-ai-guidance__intro strong,
.beta-ai-guidance__error strong,
.beta-ai-guidance__empty strong {
    color: #1a304e;
    font-size: 14px;
    font-weight: 850;
}

.beta-ai-guidance__intro p,
.beta-ai-guidance__error p,
.beta-ai-guidance__empty p {
    margin-top: 4px;
    color: #77879d;
    font-size: 11px;
    line-height: 1.5;
}

.beta-ai-guidance__error {
    border-color: #f0cccc;
    background: #fff7f7;
}

.beta-ai-guidance__error strong,
.beta-ai-guidance__error p {
    color: #984d4d;
}

.beta-ai-guidance__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
}

.beta-ai-guidance__skeleton {
    min-height: 215px;
    border-radius: 20px;
    background: linear-gradient(90deg, #f0f3f6 25%, #f8fafc 50%, #f0f3f6 75%);
    background-size: 200% 100%;
    animation: beta-ai-loading 1.4s infinite;
}

.beta-ai-guidance__meta {
    display: flex;
    gap: 10px;
    color: #91a0b3;
    font-size: 12px;
}

@keyframes beta-ai-loading {
    to {
        background-position: -200% 0;
    }
}

@media (max-width: 1000px) {
    .beta-ai-guidance__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .beta-ai-guidance__heading {
        align-items: stretch;
        flex-direction: column;
    }

    .beta-ai-guidance__grid {
        grid-template-columns: 1fr;
    }

    .beta-ai-guidance__intro,
    .beta-ai-guidance__error,
    .beta-ai-guidance__empty {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .beta-ai-guidance__intro > button,
    .beta-ai-guidance__error > button {
        grid-column: 1 / -1;
    }
}
</style>
